/**
 * B-24b WS-3 — Attention List panel.
 *
 * Wired to GET /admin/sa-dashboard/attention.
 * Empty state (status: "all_clear" OR items: []) renders a calm green "all
 * clear" banner — never a bare empty scroll region (IV-L3).
 *
 * Each row is clickable and calls onOpenDrill(tenant_id, name) so the parent
 * can launch the per-tenant drill overlay.
 */
import { useSaAttention } from "@/hooks/queries/admin/useSaDashboard";
import type { AttentionItem } from "@/lib/saDashboardApi";

interface Props {
  onOpenDrill: (tenantId: string, name: string) => void;
}

const SEVERITY_DOT: Record<AttentionItem["severity"], string> = {
  critical: "bg-brand-negative",
  warning: "bg-brand-warn",
};

export function AttentionPanel({ onOpenDrill }: Props) {
  const { data, isLoading, isError } = useSaAttention();

  return (
    <section
      data-testid="attention-panel"
      className="rounded-lg border border-border-default bg-surface-card p-4"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
          Tenant zone · sorted by severity
        </p>
      </div>
      <h3 className="mt-0.5 text-sm font-semibold text-text-primary">
        Attention list
      </h3>
      <div className="mt-3">
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-9 animate-pulse rounded bg-surface-canvas"
              />
            ))}
          </div>
        )}
        {isError && (
          <p className="text-xs text-brand-negative">
            Couldn't load attention list.
          </p>
        )}
        {data && (data.status === "all_clear" || data.items.length === 0) && (
          <AllClearBanner />
        )}
        {data && data.status === "items" && data.items.length > 0 && (
          <AttentionRows items={data.items} onOpen={onOpenDrill} />
        )}
      </div>
    </section>
  );
}

function AllClearBanner() {
  return (
    <div
      data-testid="attention-all-clear"
      className="flex items-center gap-2.5 rounded-md border border-brand-positive/40 bg-brand-positive/[0.08] px-3 py-2.5"
    >
      <span className="h-2 w-2 rounded-full bg-brand-positive" />
      <span className="text-xs font-semibold text-brand-positive">
        All clear — no tenants flagged
      </span>
    </div>
  );
}

function AttentionRows({
  items,
  onOpen,
}: {
  items: AttentionItem[];
  onOpen: (tenantId: string, name: string) => void;
}) {
  return (
    <div className="-mx-1 max-h-[260px] space-y-px overflow-y-auto">
      {items.map((item) => (
        <button
          key={item.tenant_id}
          type="button"
          onClick={() => onOpen(item.tenant_id, item.name)}
          className="grid w-full grid-cols-[10px_1fr_auto] items-start gap-2.5 rounded-md px-1 py-2 text-left transition hover:bg-surface-card-hover"
        >
          <span
            className={
              "mt-1 h-2 w-2 shrink-0 rounded-full " +
              SEVERITY_DOT[item.severity]
            }
          />
          <span className="min-w-0">
            <span className="block truncate text-xs font-medium text-text-primary">
              {item.name}
            </span>
            <span className="block truncate text-[11px] text-text-tertiary">
              {item.reason}
            </span>
          </span>
          <span className="shrink-0 rounded border border-border-default px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-text-tertiary">
            {item.recommended_action}
          </span>
        </button>
      ))}
    </div>
  );
}
